import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Mail, Instagram, Linkedin, Phone, Copy, RefreshCw, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = [
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "email", label: "Email", icon: Mail },
  { id: "instagram", label: "Instagram DM", icon: Instagram },
  { id: "linkedin", label: "LinkedIn", icon: Linkedin },
  { id: "call", label: "Cold Call", icon: Phone },
];
const ADJUSTMENTS = ["Make shorter", "Make more human", "More direct", "More premium", "More casual"];

export function OutreachStudio({ oppId, autoChannel = "whatsapp" }) {
  const [channel, setChannel] = useState(autoChannel);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  const generate = async (ch = channel, adjust = null) => {
    setLoading(true);
    try {
      const { data } = await api.post("/outreach", { opportunity_id: oppId, channel: ch, adjust });
      setMessage(data.message);
      setDirty(false);
    } catch (e) {
      toast.error("Could not generate message.");
    } finally {
      setLoading(false);
    }
  };

  const pickChannel = (ch) => { setChannel(ch); generate(ch); };
  const copy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(message);
      } else {
        const ta = document.createElement("textarea");
        ta.value = message;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy — select the text and copy manually.");
    }
  };

  return (
    <div className="space-y-4" data-testid="outreach-studio">
      <div className="flex flex-wrap gap-2">
        {CHANNELS.map((c) => (
          <button key={c.id} onClick={() => pickChannel(c.id)} data-testid={`channel-${c.id}`}
            className={`px-3 py-2 rounded-lg text-sm border flex items-center gap-2 transition-all ${channel === c.id ? "bg-primary/15 border-primary/50 text-primary" : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600"}`}>
            <c.icon className="w-4 h-4" /> {c.label}
          </button>
        ))}
      </div>

      {message === "" && !loading ? (
        <div className="rounded-xl border border-dashed border-slate-700 p-8 text-center">
          <Wand2 className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-sm text-slate-400 mb-4">Generate a human-sounding message for this opportunity.</p>
          <Button onClick={() => generate()} className="bg-primary hover:bg-emerald-600 text-white" data-testid="generate-outreach-btn">
            Generate {CHANNELS.find((c) => c.id === channel)?.label}
          </Button>
        </div>
      ) : (
        <>
          <div className="relative">
            {loading && <div className="absolute inset-0 bg-background/60 grid place-items-center rounded-xl z-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
            <Textarea value={message} onChange={(e) => { setMessage(e.target.value); setDirty(true); }} rows={channel === "email" || channel === "call" ? 10 : 6}
              className="bg-slate-900 border-slate-700 font-mono text-sm leading-relaxed" data-testid="outreach-message" />
          </div>
          <div className="flex flex-wrap gap-2">
            {ADJUSTMENTS.map((a) => (
              <button key={a} onClick={() => generate(channel, a)} disabled={loading} data-testid={`adjust-${a.split(" ")[1] || a}`}
                className="text-[11px] px-2.5 py-1 rounded-md bg-slate-800/60 border border-slate-700 text-slate-400 hover:text-primary hover:border-primary/30 disabled:opacity-50">
                {a}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={copy} className="bg-primary hover:bg-emerald-600 text-white" data-testid="copy-outreach-btn"><Copy className="w-4 h-4 mr-1.5" /> Copy{dirty ? " (edited)" : ""}</Button>
            <Button variant="outline" onClick={() => generate(channel)} disabled={loading} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="regenerate-outreach-btn"><RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate</Button>
          </div>
          <p className="text-[11px] text-slate-500">Messages are AI-drafted from observed signals — review before sending. Never make claims you can't back up.</p>
        </>
      )}
    </div>
  );
}
